import { WRITE_ROLES, type MemberRole } from '@guabi/shared';
import { useMe } from './queries';

/**
 * Whether this person may change this wedding.
 *
 * The server has always known: `requireWeddingAccess(…, 'write')` refuses every
 * role outside `WRITE_ROLES`. The browser did not, so somebody invited as
 * `family` saw every Add, Save and Delete button, filled a form, pressed Save
 * and got an error — the app offering an action it knew would be refused.
 *
 * **It is not a security boundary.** The API re-checks every write regardless.
 * This exists so a read-only member is shown a coherent app rather than a
 * minefield, which is a different job and the only one the client can do.
 *
 * Defaults to `true` while `/me` is loading: a brief flash of a button that
 * turns out to be disallowed is better than hiding the whole app from the
 * owner for a second on every page load.
 */
export function useCanWrite(wsId: string | undefined): boolean {
  const me = useMe();
  if (!me.data || !wsId) return true;

  const role: MemberRole | undefined = me.data.memberships.find((m) => m.wsId === wsId)?.role;
  // No membership row at all means a workspace that predates them being listed;
  // the API is the judge either way.
  return role === undefined || WRITE_ROLES.includes(role);
}
